import { StoreState } from '../reducers';
import {
	DownloadGroupName,
	DownloadGroupStatus,
	DownloadManagerState,
} from '../containers/DownloadManager/reducer';
import {
	DownloadStatus,
	LogoThumbState,
	OfflineItemStatus,
	OfflineManagerState,
	OfflineManagerStatus,
} from '../containers/OfflineManager/reducer';
import { buildPersistableDownloads } from '../containers/DownloadManager/actions';
import IndexedDBManager from './indexedDB/manager';
import NativeApi from './nativeApi';
import UserAgentUtils from './userAgent';

const resetDownloadingStatuses = (items: { [k: number]: OfflineItemStatus }): { [k: number]: OfflineItemStatus } => {
	const reset: { [k: number]: OfflineItemStatus } = {};

	Object.keys(items).forEach((key) => {
		const id = parseInt(key, 10);
		const item = items[id];

		// Logout interrupts in-flight work; do not persist ERROR from a cancelled session.
		if (item && (item.status === DownloadStatus.DOWNLOADING || item.status === DownloadStatus.ERROR)) {
			reset[id] = { ...item, status: DownloadStatus.NOT_DOWNLOADED };
		} else {
			reset[id] = item;
		}
	});

	return reset;
};

const resetLogoThumb = (logo: LogoThumbState): LogoThumbState => {
	const reset = (s: LogoThumbState['localConfig']): LogoThumbState['localConfig'] =>
		s.status === DownloadStatus.DOWNLOADING ? { ...s, status: DownloadStatus.NOT_DOWNLOADED } : s;

	return { localConfig: reset(logo.localConfig), remoteConfig: reset(logo.remoteConfig) };
};

const buildOfflineManagerLogoutSnapshot = (offlineManager: OfflineManagerState): OfflineManagerState => ({
	...offlineManager,
	thumbMap: resetDownloadingStatuses(offlineManager.thumbMap),
	requiredItems: resetDownloadingStatuses(offlineManager.requiredItems),
	bgMediaMap: resetDownloadingStatuses(offlineManager.bgMediaMap),
	brandThumb: resetLogoThumb(offlineManager.brandThumb),
	homeThumb: resetLogoThumb(offlineManager.homeThumb),
	defaultFolderIcon: offlineManager.defaultFolderIcon.status === DownloadStatus.DOWNLOADING
		? { ...offlineManager.defaultFolderIcon, status: DownloadStatus.NOT_DOWNLOADED }
		: offlineManager.defaultFolderIcon,
	status: OfflineManagerStatus.NOT_INITIALIZED,
	isPreparingContentStructure: false,
	isContentStructureToastClosed: false,
	isDownloadingThumbs: false,
	isDownloadingRequiredItems: false,
	isReturnVisitOfflineInit: false,
	isSynchronizing: false,
});

export const stopActiveNativeDownloads = (downloadManager: DownloadManagerState): void => {
	Object.keys(downloadManager.items).forEach((key) => {
		const item = downloadManager.items[parseInt(key, 10)];

		if (item && item.requestId !== undefined) {
			NativeApi.stopDownload(item.requestId);
		}
	});
};

export const persistLogoutSnapshot = async (state: StoreState): Promise<void> => {
	if (!UserAgentUtils.isMobileApp || !UserAgentUtils.isOnline()) {
		return;
	}

	const { downloadManager, offlineManager } = state;

	await IndexedDBManager.setDownloads(buildPersistableDownloads(downloadManager.items));
	await IndexedDBManager.setStoreOfflineManager(buildOfflineManagerLogoutSnapshot(offlineManager));

	await Promise.all([
		IndexedDBManager.setDownloadGroups(DownloadGroupName.ALL, DownloadGroupStatus.NotStarted),
		IndexedDBManager.setDownloadGroups(DownloadGroupName.IMPORTANT, DownloadGroupStatus.NotStarted),
		IndexedDBManager.setDownloadGroups(DownloadGroupName.SELECTED, DownloadGroupStatus.NotStarted),
	]);
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/logoutSessionCleanup.ts